import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect } from "react";
import { Bold, Italic, UnderlineIcon, List, ListOrdered, Link2, Code, Heading1, Heading2, Undo2, Redo2, Pilcrow } from "lucide-react";
import { cn } from "@/lib/utils";

const ToolbarBtn = ({ active, onClick, children, label }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    className={cn(
      "h-8 w-8 inline-flex items-center justify-center rounded text-slate-600 hover:bg-slate-100 transition-colors",
      active && "bg-indigo-100 text-indigo-700"
    )}
  >
    {children}
  </button>
);

export default function TiptapEditor({ value, onChange, minHeight = 280 }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder: "Write your email body... use the variable chips to insert dynamic values." }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none p-4 min-h-[280px] text-slate-800",
        "data-testid": "tiptap-editor",
      },
    },
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
  });

  // Keep external value in sync (e.g., when switching templates)
  useEffect(() => {
    if (!editor) return;
    if ((value || "") !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const insertText = (s) => editor.chain().focus().insertContent(s).run();
  // exposed via ref-less prop pattern
  TiptapEditor.lastEditor = editor;

  const isActive = (m, opts) => editor.isActive(m, opts);

  const promptLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="border border-slate-200 rounded-lg bg-white" data-testid="tiptap-wrapper">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50/60 rounded-t-lg">
        <ToolbarBtn active={isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} label="Bold">
          <Bold className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn active={isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} label="Italic">
          <Italic className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn active={isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} label="Underline">
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarBtn>
        <div className="w-px h-5 bg-slate-200 mx-1.5" />
        <ToolbarBtn active={isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} label="Heading 1">
          <Heading1 className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn active={isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} label="Heading 2">
          <Heading2 className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn active={isActive("paragraph")} onClick={() => editor.chain().focus().setParagraph().run()} label="Paragraph">
          <Pilcrow className="w-4 h-4" />
        </ToolbarBtn>
        <div className="w-px h-5 bg-slate-200 mx-1.5" />
        <ToolbarBtn active={isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} label="Bullet list">
          <List className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn active={isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} label="Numbered list">
          <ListOrdered className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn active={isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()} label="Code block">
          <Code className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn active={isActive("link")} onClick={promptLink} label="Insert link">
          <Link2 className="w-4 h-4" />
        </ToolbarBtn>
        <div className="ml-auto flex items-center gap-0.5">
          <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} label="Undo"><Undo2 className="w-4 h-4" /></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} label="Redo"><Redo2 className="w-4 h-4" /></ToolbarBtn>
        </div>
      </div>
      <EditorContent editor={editor} style={{ minHeight }} />
    </div>
  );
}

/** Helper for parent to inject variable placeholder at cursor. */
export function insertVariable(token) {
  const ed = TiptapEditor.lastEditor;
  if (!ed) return;
  ed.chain().focus().insertContent(`{{ ${token} }}`).run();
}
